import { useEffect, useRef, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { getMember, memberPhotoUrl, updateMember } from "../../services/memberService"
import { processMemberPhotoFile } from "../../utils/memberPhotoCapture"
import { getSubscriptionAccessAlert } from "../../utils/subscriptionAlerts"
import { QRCodeCanvas } from "qrcode.react"
import { ArrowLeft, CreditCard, Download, Pencil, ImagePlus } from "lucide-react"
import { fr } from "../../locales/fr"
import {
  genderLabel,
  subscriptionStatusLabel,
  paymentMethodLabel,
  paymentTypeLabel,
  accessStatusLabel,
  accessMethodLabel,
} from "../../locales/fr"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/Card"
import { Button } from "../../components/ui/Button"
import { Badge } from "../../components/ui/Badge"
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui/Table"
import { PaymentModal } from "../../components/members/PaymentModal"
import { PaymentInvoiceModal } from "../../components/payments/PaymentInvoiceModal"
import SelfieQrPanel from "../../components/members/SelfieQrPanel"
import { downloadPaymentInvoice } from "../../services/paymentService"

/** Cartes lisibles en thème sombre : couleurs explicites (évite texte clair sur fond clair si les vars CSS dérivent). */
const cardClass =
  "border border-slate-700 bg-slate-900 text-slate-100 shadow-sm [&_.text-muted-foreground]:text-slate-400"

function InfoRow({ label, value }) {
  const display = value != null && String(value).trim() !== "" ? value : "—"
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-slate-700/60 py-3 last:border-0 sm:grid-cols-3 sm:items-center">
      <dt className="text-sm font-medium text-slate-400">{label}</dt>
      <dd className="text-sm text-slate-100 sm:col-span-2">{display}</dd>
    </div>
  )
}

export default function MemberView() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [profile, setProfile] = useState(null)
  const [paymentOpen, setPaymentOpen] = useState(false)
  const [paymentToast, setPaymentToast] = useState("")
  const [invoiceModalId, setInvoiceModalId] = useState(null)
  const [invoiceDownloadError, setInvoiceDownloadError] = useState("")
  const [accessSaving, setAccessSaving] = useState(false)
  const [accessError, setAccessError] = useState("")
  const [photoBusy, setPhotoBusy] = useState(false)
  const [photoMsg, setPhotoMsg] = useState("")
  const [photoErr, setPhotoErr] = useState("")
  const photoInputRef = useRef(null)

  useEffect(() => {
    getMember(id).then((data) => {
      setProfile(data)
    })
  }, [id])

  const d = fr.memberDetail
  const t = fr.tables

  if (!profile) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-slate-400">
        {fr.common.loading}
      </div>
    )
  }

  const { member, subscriptions, payments, accessLogs, paymentSummary } = profile
  const balance = paymentSummary ?? { totalPaid: 0, totalRemaining: 0 }
  const photoSrc = memberPhotoUrl(member.photo)
  const subAlert = getSubscriptionAccessAlert(subscriptions)

  const formatFrDate = (ymd) => {
    if (!ymd) return ""
    const p = String(ymd).split("-").map(Number)
    if (p.length < 3) return ymd
    return new Date(p[0], p[1] - 1, p[2]).toLocaleDateString("fr-FR")
  }

  const subscriptionAlertText = () => {
    if (!subAlert) return ""
    const date = formatFrDate(subAlert.endDate)
    const key =
      subAlert.level === "expired"
        ? "subscriptionAlertExpired"
        : subAlert.level === "1d"
          ? "subscriptionAlert1d"
          : subAlert.level === "7d"
            ? "subscriptionAlert7d"
            : "subscriptionAlert30d"
    return String(d[key]).replace("{{date}}", date)
  }

  const subscriptionAlertClass =
    subAlert == null
      ? ""
      : subAlert.level === "expired"
        ? "border-red-500/50 bg-red-950/40 text-red-100"
        : subAlert.level === "1d"
          ? "border-orange-500/50 bg-orange-950/40 text-orange-100"
          : subAlert.level === "7d"
            ? "border-amber-500/50 bg-amber-950/35 text-amber-100"
            : "border-yellow-500/45 bg-yellow-950/25 text-yellow-100"

  const onToggleAccessBlocked = async (e) => {
    const next = e.target.checked
    setAccessError("")
    setAccessSaving(true)
    try {
      await updateMember(id, { accessBlocked: next })
      const data = await getMember(id)
      setProfile(data)
    } catch {
      setAccessError(d.accessUpdateError)
      e.target.checked = !next
    } finally {
      setAccessSaving(false)
    }
  }

  const onPhotoFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return

    setPhotoBusy(true)
    setPhotoMsg("")
    setPhotoErr("")
    try {
      const out = await processMemberPhotoFile(file)
      const payload = { photo: out.photo }
      if (out.faceDescriptor) payload.faceDescriptor = out.faceDescriptor
      await updateMember(id, payload)
      const data = await getMember(id)
      setProfile(data)
      setPhotoMsg(out.faceFound ? d.photoUploadOk : d.photoUploadOkNoFace)
    } catch {
      setPhotoErr(d.photoUploadError)
    } finally {
      setPhotoBusy(false)
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 text-slate-100">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Button variant="outline" size="sm" onClick={() => navigate("/members")}>
          <ArrowLeft className="h-4 w-4" />
          {d.backToList}
        </Button>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setPaymentOpen(true)}>
            <CreditCard className="h-4 w-4" />
            {fr.common.recordPayment}
          </Button>
          <Button size="sm" onClick={() => navigate(`/members/edit/${id}`)}>
            <Pencil className="h-4 w-4" />
            {d.editProfile}
          </Button>
        </div>
      </div>

      {paymentToast ? (
        <div className="rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-sm text-success">
          {paymentToast}
        </div>
      ) : null}

      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-50">{d.profileTitle}</h1>
        <p className="mt-1 text-sm text-slate-400">
          {d.memberNumberLabel} : {member.memberNumber}
        </p>
      </div>

      {member.accessBlocked ? (
        <div
          className="rounded-lg border border-red-500/50 bg-red-950/40 px-4 py-3 text-sm text-red-100"
          role="status"
        >
          {d.accessBlockedBanner}
        </div>
      ) : null}

      {subAlert ? (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${subscriptionAlertClass}`}
          role="status"
        >
          {subscriptionAlertText()}
        </div>
      ) : null}

      <Card className={cardClass}>
        <CardHeader className="border-b border-slate-700 pb-4">
          <CardTitle className="text-slate-50">{d.accessGymTitle}</CardTitle>
          <CardDescription className="!text-slate-400">{d.accessBlockedHint}</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-slate-500 bg-slate-900 text-primary focus:ring-ring"
              checked={!!member.accessBlocked}
              disabled={accessSaving}
              onChange={onToggleAccessBlocked}
            />
            <span className="text-sm leading-snug text-slate-200">{d.accessBlockedLabel}</span>
          </label>
          {accessError ? (
            <p className="mt-2 text-sm text-red-400">{accessError}</p>
          ) : null}
        </CardContent>
      </Card>

      <Card className={cardClass}>
        <CardHeader className="border-b border-slate-700 pb-4">
          <CardTitle className="text-slate-50">{d.infoTitle}</CardTitle>
          <CardDescription className="!text-slate-400">{d.infoDescription}</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
            <div className="flex shrink-0 flex-col items-center gap-3 lg:items-start">
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPhotoFile}
              />
              {photoSrc ? (
                <img
                  src={photoSrc}
                  alt=""
                  className="h-32 w-32 rounded-2xl border border-slate-600 object-cover shadow-sm"
                />
              ) : (
                <div className="flex h-32 w-32 items-center justify-center rounded-2xl border border-dashed border-slate-600 bg-slate-800 text-2xl font-semibold text-slate-400">
                  {(member.fullName || member.firstName || "?").charAt(0)}
                </div>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={photoBusy}
                className="gap-2 border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700"
                onClick={() => photoInputRef.current?.click()}
              >
                <ImagePlus className="h-4 w-4" />
                {photoBusy ? d.photoUploading : photoSrc ? d.photoChange : d.photoUpload}
              </Button>
              {photoMsg ? <p className="max-w-[12rem] text-center text-xs text-emerald-400">{photoMsg}</p> : null}
              {photoErr ? <p className="max-w-[12rem] text-center text-xs text-red-400">{photoErr}</p> : null}
              <div className="w-full max-w-xs">
                <SelfieQrPanel
                  memberId={member.id}
                  currentPhoto={member.photo}
                  dark
                  compact
                  onPhotoReceived={(data) => {
                    setProfile(data)
                    setPhotoMsg(d.photoUploadOk)
                  }}
                />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <p className="mb-4 text-xl font-semibold text-slate-50">{member.fullName}</p>
              <dl>
                <InfoRow label={d.phone} value={member.phone} />
                <InfoRow label={d.email} value={member.email} />
                <InfoRow label={d.cin} value={member.cin} />
                <InfoRow label={d.gender} value={genderLabel(member.gender)} />
                <InfoRow label={d.birthDate} value={member.birthDate} />
                <InfoRow label={d.profession} value={member.profession} />
                <InfoRow label={d.address} value={member.address} />
              </dl>
            </div>

            <div className="flex shrink-0 flex-col items-center justify-center rounded-xl border border-slate-600 bg-slate-800/80 px-6 py-6 lg:border-l lg:pl-8">
              <p className="mb-3 text-xs font-medium uppercase tracking-wide text-slate-400">
                {d.qrLabel}
              </p>
              <div className="rounded-lg bg-white p-2 ring-1 ring-slate-300">
                <QRCodeCanvas
                  value={member.memberNumber || member.qrCode || ""}
                  size={140}
                />
              </div>
              {d.qrHint ? (
                <p className="mt-2 max-w-[10rem] text-center text-[11px] leading-snug text-slate-400">
                  {d.qrHint}
                </p>
              ) : null}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className={cardClass}>
        <CardHeader className="border-b border-slate-700 pb-4">
          <CardTitle className="text-slate-50">{d.paymentBalanceTitle}</CardTitle>
          <CardDescription className="!text-slate-400">{d.paymentBalanceHint}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6 pt-6 sm:grid-cols-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
              {d.totalPaidAll}
            </p>
            <p className="mt-1 text-2xl font-semibold text-emerald-400">
              {Number(balance.totalPaid).toLocaleString()} MAD
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
              {d.totalRemainingAll}
            </p>
            <p className="mt-1 text-2xl font-semibold text-amber-200">
              {Number(balance.totalRemaining).toLocaleString()} MAD
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className={cardClass}>
        <CardHeader>
          <CardTitle className="text-slate-50">{d.subscriptions}</CardTitle>
        </CardHeader>
        <CardContent className="p-0 sm:px-6 sm:pb-6 [&_thead]:border-slate-700 [&_thead]:bg-slate-800/80">
          {subscriptions.length === 0 ? (
            <p className="px-6 py-8 text-center text-sm text-slate-400">{d.noSubscriptions}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-slate-800/50">
                  <TableHead className="text-slate-200">{t.pack}</TableHead>
                  <TableHead className="text-slate-200">{t.plan}</TableHead>
                  <TableHead className="text-slate-200">{t.start}</TableHead>
                  <TableHead className="text-slate-200">{t.end}</TableHead>
                  <TableHead className="text-slate-200">{fr.members.table.status}</TableHead>
                  <TableHead className="text-right text-slate-200">{t.price}</TableHead>
                  <TableHead className="text-right text-slate-200">{d.subscriptionPaidCol}</TableHead>
                  <TableHead className="text-right text-slate-200">{d.subscriptionRemainingCol}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscriptions.map((sub) => (
                  <TableRow key={sub.id} className="border-slate-700 hover:bg-slate-800/40">
                    <TableCell className="text-slate-100">{sub.pack}</TableCell>
                    <TableCell className="text-slate-300">{sub.plan}</TableCell>
                    <TableCell className="text-slate-300">{sub.startDate}</TableCell>
                    <TableCell className="text-slate-300">{sub.endDate}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          sub.status === "active"
                            ? "success"
                            : sub.status === "expired"
                              ? "destructive"
                              : "secondary"
                        }
                      >
                        {subscriptionStatusLabel(sub.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium text-slate-100">{sub.price} MAD</TableCell>
                    <TableCell className="text-right text-emerald-300/90">
                      {Number(sub.paid ?? 0).toLocaleString()} MAD
                    </TableCell>
                    <TableCell className="text-right text-amber-200/90">
                      {Number(sub.remaining ?? 0).toLocaleString()} MAD
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className={cardClass}>
        <CardHeader className="border-b border-slate-700 pb-4">
          <CardTitle className="text-slate-50">{d.payments}</CardTitle>
          <CardDescription className="!text-slate-400">{d.paymentsHint}</CardDescription>
        </CardHeader>
        <CardContent className="p-0 sm:px-6 sm:pb-6 [&_thead]:border-slate-700 [&_thead]:bg-slate-800/80">
          {invoiceDownloadError ? (
            <div className="mx-4 mt-4 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-red-300">
              {invoiceDownloadError}
            </div>
          ) : null}
          {payments.length === 0 ? (
            <p className="px-6 py-8 text-center text-sm text-slate-400">{d.noPayments}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-right text-slate-200">{t.amount}</TableHead>
                    <TableHead className="text-slate-200">{t.method}</TableHead>
                    <TableHead className="text-slate-200">{t.paymentType}</TableHead>
                    <TableHead className="text-slate-200">{t.date}</TableHead>
                    <TableHead className="text-slate-200">{d.invoiceNumberCol}</TableHead>
                    <TableHead className="text-right text-slate-200 min-w-[200px]">{t.invoice}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((p) => (
                    <TableRow key={p.id} className="border-slate-700 hover:bg-slate-800/40">
                      <TableCell className="text-right font-medium text-emerald-400">{p.amount} MAD</TableCell>
                      <TableCell className="text-slate-100">{paymentMethodLabel(p.method)}</TableCell>
                      <TableCell className="text-slate-300">{paymentTypeLabel(p.paymentType)}</TableCell>
                      <TableCell className="text-slate-300 whitespace-nowrap">{p.paidAt}</TableCell>
                      <TableCell className="font-mono text-sm text-slate-300">
                        {p.invoiceNumber || `REF-${p.id}`}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:justify-end sm:items-center">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="gap-2 border-slate-600 text-slate-100 hover:bg-slate-800"
                            title={d.downloadInvoice}
                            onClick={async () => {
                              setInvoiceDownloadError("")
                              try {
                                await downloadPaymentInvoice(p.id)
                              } catch {
                                setInvoiceDownloadError(fr.invoice.downloadError)
                              }
                            }}
                          >
                            <Download className="h-4 w-4 shrink-0" />
                            <span className="text-left sm:text-center">{d.downloadInvoice}</span>
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-slate-300 hover:bg-slate-800 hover:text-slate-50"
                            onClick={() => setInvoiceModalId(p.id)}
                          >
                            <Pencil className="mr-2 h-4 w-4" />
                            {fr.invoice.editInvoice}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className={cardClass}>
        <CardHeader>
          <CardTitle className="text-slate-50">{d.accessHistory}</CardTitle>
        </CardHeader>
        <CardContent className="p-0 sm:px-6 sm:pb-6 [&_thead]:border-slate-700 [&_thead]:bg-slate-800/80">
          {accessLogs.length === 0 ? (
            <p className="px-6 py-8 text-center text-sm text-slate-400">{d.noAccessLogs}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-200">{t.method}</TableHead>
                  <TableHead className="text-slate-200">{fr.members.table.status}</TableHead>
                  <TableHead className="text-slate-200">{t.date}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accessLogs.map((a) => (
                  <TableRow key={a.id} className="border-slate-700 hover:bg-slate-800/40">
                    <TableCell className="text-slate-100">{accessMethodLabel(a.method)}</TableCell>
                    <TableCell>
                      <Badge variant={a.status === "granted" ? "success" : "destructive"}>
                        {accessStatusLabel(a.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-300">{a.createdAt}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PaymentModal
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        memberId={Number(id)}
        subscriptions={subscriptions}
        onSuccess={() => {
          getMember(id).then(setProfile)
          setPaymentToast(d.paymentSuccess)
        }}
      />

      <PaymentInvoiceModal
        open={invoiceModalId != null}
        onClose={() => setInvoiceModalId(null)}
        paymentId={invoiceModalId}
        onSaved={() => getMember(id).then(setProfile)}
      />
    </div>
  )
}
